import { useEffect, useState } from 'react';
import {
  Globe,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  Link2,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  fetchGroupedUrls,
  syncSites,
  deleteSyncedUrl,
  deleteSyncedDomain,
  type DomainGroup,
  type SyncResult,
} from '@/api/admin';

function normalizeUrl(url: string): string {
  return url.trim().replace(/^["']|["']$/g, '').trim();
}

function formatDate(dateString: string): string {
  if (!dateString || dateString === 'N/A') return '--';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

export default function SiteSyncTab() {
  const [domains, setDomains] = useState<Record<string, DomainGroup>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  // Add URLs state
  const [urlInput, setUrlInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);

  // Delete state
  const [deleteUrlTarget, setDeleteUrlTarget] = useState<string | null>(null);
  const [deleteDomainTarget, setDeleteDomainTarget] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchGroupedUrls();
      setDomains(data.domains || {});
    } catch {
      toast.error('Failed to load indexed sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleDomain = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const handleSync = async () => {
    const urlList = urlInput
      .split(/[\n,]+/)
      .map(normalizeUrl)
      .filter((u) => u.length > 0);

    if (urlList.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    setSyncing(true);
    setSyncResults([]);

    try {
      const data = await syncSites(urlList, 'scrape');
      setSyncResults(data.results || []);

      const success = data.results.filter((r) => r.status === 'success').length;
      const failed = data.results.filter((r) => r.status === 'failed').length;
      const skipped = data.results.filter((r) => r.status === 'skipped').length;

      if (success > 0) {
        toast.success(`Done: ${success} scraped, ${failed} failed, ${skipped} skipped`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} URL(s) already indexed`);
      } else {
        toast.error(`All ${failed} URL(s) failed`);
      }

      await load();
    } catch {
      toast.error('Scraping failed. Check your connection and try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteUrl = async () => {
    if (!deleteUrlTarget) return;
    try {
      const result = await deleteSyncedUrl(deleteUrlTarget);
      toast.success(result.message);
      setDeleteUrlTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete URL');
    }
  };

  const handleDeleteDomain = async () => {
    if (!deleteDomainTarget) return;
    try {
      const result = await deleteSyncedDomain(deleteDomainTarget);
      toast.success(result.message);
      setDeleteDomainTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete domain');
    }
  };

  const filteredDomains = Object.entries(domains).filter(([domain, data]) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (domain.toLowerCase().includes(q)) return true;
    return data.sub_pages.some((p) => p.url.toLowerCase().includes(q));
  });

  const totalPages = Object.values(domains).reduce(
    (sum, d) => sum + d.sub_pages.length,
    0
  );
  const totalDocs = Object.values(domains).reduce(
    (sum, d) => sum + d.total_documents,
    0
  );

  const parsedUrlCount = urlInput
    .split(/[\n,]+/)
    .map(normalizeUrl)
    .filter((u) => u.length > 0).length;

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <p className="text-sm text-muted-foreground">Domains</p>
          <p className="text-2xl font-bold">{Object.keys(domains).length}</p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <p className="text-sm text-muted-foreground">Pages</p>
          <p className="text-2xl font-bold">{totalPages}</p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <p className="text-sm text-muted-foreground">Document Chunks</p>
          <p className="text-2xl font-bold">{totalDocs}</p>
        </div>
      </div>

      {/* Indexed sites */}
      <div className="rounded-lg border">
        <div className="flex items-center justify-between gap-4 border-b p-4">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold">Indexed Sites</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="divide-y">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-md" />
              ))}
            </div>
          ) : filteredDomains.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Globe className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">
                {searchQuery ? 'No sites match your search' : 'No sites indexed yet'}
              </p>
            </div>
          ) : (
            filteredDomains.map(([domain, data]) => (
              <div key={domain}>
                {/* Domain row */}
                <div
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => toggleDomain(domain)}
                >
                  {expandedDomains.has(domain) ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <Globe className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="font-medium text-sm truncate flex-1">
                    {domain}
                  </span>
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    {data.sub_pages.length} {data.sub_pages.length === 1 ? 'page' : 'pages'}
                  </Badge>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {data.total_documents} chunks
                  </Badge>
                  <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">
                    {formatDate(data.indexed_date)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDomainTarget(domain);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Expanded sub-pages */}
                {expandedDomains.has(domain) && (
                  <div className="bg-muted/20 border-t">
                    {data.sub_pages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No pages found
                      </p>
                    ) : (
                      <div className="divide-y max-h-80 overflow-y-auto">
                        {data.sub_pages.map((page) => (
                          <div
                            key={page.url}
                            className="flex items-center gap-2 px-4 py-2.5 pl-12 hover:bg-muted/30 transition-colors group"
                          >
                            <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <a
                              href={page.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate flex-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {page.url}
                            </a>
                            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            <Badge variant="secondary" className="text-xs shrink-0">
                              {page.count} {page.count === 1 ? 'chunk' : 'chunks'}
                            </Badge>
                            <span className="text-xs text-muted-foreground shrink-0 hidden md:inline">
                              {formatDate(page.indexed_date)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteUrlTarget(page.url);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add new sites */}
      <div className="rounded-lg border">
        <div className="flex items-center gap-2 border-b p-4">
          <Plus className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Scrape New Sites</h3>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <textarea
              placeholder={
                'Paste URLs here, one per line:\nhttps://ipma.world/about\nhttps://ipma.world/certification'
              }
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              rows={5}
              disabled={syncing}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              One URL per line. Supports quoted and unquoted URLs. Protocol is auto-added if missing.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {parsedUrlCount} URL{parsedUrlCount !== 1 ? 's' : ''} entered
            </span>
            <Button onClick={handleSync} disabled={syncing || !urlInput.trim()}>
              {syncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scraping...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Start Scraping
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Sync results */}
      {syncResults.length > 0 && (
        <div className="rounded-lg border">
          <div className="flex items-center justify-between border-b p-4">
            <h3 className="font-semibold">Scrape Results</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSyncResults([])}
              className="text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
          <div className="divide-y">
            {syncResults.map((result, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 px-4 py-3 ${
                  result.status === 'success'
                    ? 'bg-green-50/50 dark:bg-green-950/20'
                    : result.status === 'skipped'
                      ? 'bg-amber-50/50 dark:bg-amber-950/20'
                      : 'bg-red-50/50 dark:bg-red-950/20'
                }`}
              >
                {result.status === 'success' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                ) : result.status === 'skipped' ? (
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium break-all">{result.url}</p>
                  {result.message && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {result.message}
                    </p>
                  )}
                </div>
                {result.document_count != null && (
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    {result.document_count} docs
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete URL dialog */}
      <AlertDialog
        open={!!deleteUrlTarget}
        onOpenChange={(open) => !open && setDeleteUrlTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this URL?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all document chunks scraped from this URL.
              <br />
              <span className="font-medium break-all block mt-2">
                {deleteUrlTarget}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUrl}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete domain dialog */}
      <AlertDialog
        open={!!deleteDomainTarget}
        onOpenChange={(open) => !open && setDeleteDomainTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entire domain?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove ALL pages and chunks from{' '}
              <span className="font-semibold">{deleteDomainTarget}</span>.
              {deleteDomainTarget && domains[deleteDomainTarget] && (
                <span className="block mt-2 text-sm">
                  {domains[deleteDomainTarget].sub_pages.length} pages,{' '}
                  {domains[deleteDomainTarget].total_documents} document chunks will be
                  deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDomain}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
